import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { RouteConfig } from '../../../interfaces/routes-config.interface';
import { APP_ROUTES, PUBLIC_PATH } from '../../../config/routes-config';
import { ReservationChannel } from '../../../enums/reservation-channel.enum';
import { ReservationSource } from '../../../enums/reservation-souce.enum';
import { ReservationUiModel } from '../../../ui-models/reservation.ui-model';
import { BlockedDatesUiModel } from '../../../ui-models/blocked-dates.ui-model';

export type DoubleBookingItem = {
  currentReservation: ReservationUiModel;
  otherReservation?: ReservationUiModel;
  blockedDate?: BlockedDatesUiModel;
};

type Props = {
  doubleBookings: DoubleBookingItem[];
  onClosePopover: () => void;
  onShowDetails: () => void;
};

export const ChannelLogos = ({
  source,
  channel
}: {
  source?: ReservationSource | string;
  channel?: ReservationChannel | string;
}) => (
  <div className="flex items-center gap-1.5 text-[1.1rem]">
    {source === ReservationSource.BN_ADMIN ? (
      <img
        className="h-6 w-6 object-contain"
        src={`${PUBLIC_PATH}/assets/jpeg/admin.webp`}
        alt="Admin"
      />
    ) : source === ReservationSource.BN_APP ? (
      <img
        className="h-6 w-14 object-contain"
        src={`${PUBLIC_PATH}/assets/pngs/bn-app.png`}
        alt="BN App"
      />
    ) : source === ReservationSource.BN_WEBSITE ? (
      <img
        className="h-6 w-14 object-contain"
        src={`${PUBLIC_PATH}/assets/pngs/bn-website.png`}
        alt="BN Website"
      />
    ) : source === ReservationSource.CHANNEL_MANAGER ? (
      <img
        className="h-6 w-6 object-contain"
        src={`${PUBLIC_PATH}/assets/jpeg/rentals-united.jpeg`}
        alt="Channel Manager"
      />
    ) : (
      <img
        className="h-6 w-6 object-contain"
        src={`${PUBLIC_PATH}/assets/pngs/question-mark.png`}
        alt="Unknown"
      />
    )}

    <span className="text-neutral-400">→</span>

    {channel === ReservationChannel.AIRBNB ? (
      <img
        className="h-6 w-6 object-contain"
        src={`${PUBLIC_PATH}/assets/pngs/airbnb.png`}
        alt="Airbnb"
      />
    ) : channel === ReservationChannel.EXPEDIA ? (
      <img
        className="h-6 w-6 object-contain"
        src={`${PUBLIC_PATH}/assets/pngs/expedia.png`}
        alt="Expedia"
      />
    ) : channel === ReservationChannel.BOOKING ? (
      <img
        className="h-6 w-6 object-contain"
        src={`${PUBLIC_PATH}/assets/pngs/booking.png`}
        alt="Booking"
      />
    ) : channel === ReservationChannel.VRBO ? (
      <img
        className="h-6 w-6 object-contain"
        src={`${PUBLIC_PATH}/assets/pngs/vrbo.png`}
        alt="Vrbo"
      />
    ) : (
      <img
        className="h-6 w-10 object-contain"
        src={`${PUBLIC_PATH}/assets/pngs/birdnest-logo.png`}
        alt="Birdnest"
      />
    )}
  </div>
);

export const formatDate = (dateMs?: number) => {
  if (!dateMs) return '-';
  return new Date(dateMs).toDateString();
};

const formatReservationCalledAt = (createdAt?: string) => {
  if (!createdAt) return '-';
  const date = new Date(createdAt);
  if (Number.isNaN(date.getTime())) return createdAt;
  return date.toLocaleString();
};

const reservationNumber = (reservation?: Partial<ReservationUiModel>) => {
  const maybeReservationNumber = (reservation as any)?.reservationNumber as string | undefined;
  return maybeReservationNumber || reservation?.id || '-';
};

export default function DoubleBookingsPopover({
  doubleBookings,
  onClosePopover,
  onShowDetails
}: Props) {
  const doubleBookingsCount = useMemo(() => doubleBookings?.length || 0, [doubleBookings]);

  const openReservationPath = (reservationId?: string) => {
    if (!reservationId) return '';
    return (
      (APP_ROUTES.RESERVATION_MODULE.CHILDREN as RouteConfig).EDIT.FULL_PATH as string
    ).replace(':id', reservationId);
  };

  const openBlockedDatesPath = () => {
    return (APP_ROUTES.BLOCK_DATES_MODULE.CHILDREN as RouteConfig).LIST.FULL_PATH as string;
  };

  return (
    <div className="w-[220px] max-w-[90vw] p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="mt-1 text-lg text-neutral-600">
            Double bookings: {doubleBookingsCount}
          </div>
        </div>

        <button
          type="button"
          className="shrink-0 rounded-md bg-primary-600 px-3 py-1.5 text-base font-semibold text-white hover:bg-primary-700"
          onClick={() => {
            onClosePopover();
            onShowDetails();
          }}
        >
          Show details
        </button>
      </div>

      <div className="mt-3">
        {doubleBookingsCount === 0 ? (
          <div className="text-xl text-neutral-500">No double bookings found.</div>
        ) : null}
      </div>

      {doubleBookingsCount > 0 && (
        <div className="mt-3">
          <div className="max-h-[280px] space-y-2 overflow-y-auto rounded-md border border-gray-200 bg-white p-2">
            {doubleBookings.slice(0, 6).map((booking, index) => {
              const current = booking.currentReservation;
              const other = booking.otherReservation;
              const blockedDate = booking.blockedDate;

              return (
                <div
                  key={`${current?.id || 'unknown'}-${other?.id || blockedDate?.id || index}`}
                  className="rounded-md border border-gray-100 p-2"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="truncate text-xl font-semibold text-neutral-800">
                        {booking?.currentReservation.unitName}
                      </div>
                      <div className="mt-0.5 text-base text-neutral-500">
                        {formatReservationCalledAt(current?.createdAt)}
                      </div>
                    </div>
                  </div>

                  <div className="mt-2 flex flex-col gap-2 text-xl text-neutral-700">
                    <div className="flex flex-row gap-1">
                      <div className="rounded bg-gray-50 p-2 flex items-center gap-1">
                        <div className="text-xl text-neutral-500">Current</div>
                        <Link
                          to={{ pathname: openReservationPath(current?.id) }}
                          target="_blank"
                          className="mt-0.5 text-xl block font-semibold text-primary-700 hover:underline"
                          onClick={onClosePopover}
                        >
                          #{reservationNumber(current)}
                        </Link>
                      </div>
                      <div className="rounded bg-gray-50 p-2 flex items-center gap-1">
                        <div className="flex items-center justify-between gap-2">
                          <div className="text-xl text-neutral-500">Other</div>
                        </div>
                        <Link
                          to={{ pathname: openReservationPath(other?.id) }}
                          className="mt-0.5 block text-xl font-semibold text-primary-700 hover:underline"
                          onClick={onClosePopover}
                        >
                          #{reservationNumber(other)}
                        </Link>
                      </div>
                    </div>
                    {blockedDate ? (
                      <div className="rounded bg-gray-50 p-2">
                        <div className="text-lg text-neutral-500">Blocked dates</div>
                        <Link
                          to={{ pathname: openBlockedDatesPath() }}
                          className="mt-0.5 block font-semibold text-primary-700 hover:underline"
                          onClick={onClosePopover}
                        >
                          #{blockedDate.id}
                        </Link>
                        <div className="mt-0.5 text-neutral-600">
                          {formatDate(blockedDate?.fromDate)} → {formatDate(blockedDate?.toDate)}
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>

          {doubleBookingsCount > 6 && (
            <div className="mt-2 text-xs text-neutral-500">
              Showing first 6 items. Use “Show details” for full list.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
